/**
 * Cookie-based Storage Repository for Saleor Auth SDK
 *
 * Implements the StorageRepository interface to store auth tokens in cookies
 * instead of localStorage, enabling SSR access to authentication state.
 *
 * Part of BEC-20884: Cookie persistence for Saleor Auth token
 *
 * ## Security Considerations
 *
 * ### Option A: JS-Accessible Cookies (Current Implementation)
 * - Secure + SameSite=Strict
 * - Client can read/write tokens directly
 * - Simpler implementation, works with existing @saleor/auth-sdk flow
 * - Trade-off: Tokens accessible to client-side JS (XSS risk)
 *
 * ### Option B: HttpOnly Cookies (Future consideration)
 * - HttpOnly + Secure + SameSite=Strict
 * - Would require server-side API routes to handle token operations
 * - More secure but requires architectural changes
 * - The @saleor/auth-sdk would need a custom fetch that goes through our API
 *
 * ## Cookie Size Limits
 * - JWT tokens are typically ~1-2KB
 * - Cookie limit is ~4KB per cookie
 * - Refresh tokens are typically smaller (~200-500 bytes)
 * - Current implementation should be well within limits
 *
 * ## Cookie Naming Convention
 * The @saleor/auth-sdk uses the Saleor API URL as a prefix for all storage keys:
 * - '{API_URI}+saleor_auth_module_auth_state' for auth state ("signedIn"/"signedOut")
 * - '{API_URI}+saleor_auth_module_refresh_token' for the actual refresh token JWT
 * - '{API_URI}+saleor_auth_access_token' for the access token JWT
 *
 * When stored in cookies, special characters are sanitized to underscores.
 * e.g., 'https://api.example.com/graphql/+saleor_auth_module_refresh_token'
 * becomes 'https___api_example_com_graphql__saleor_auth_module_refresh_token'
 */

import cookie from 'cookie';
import type { IncomingMessage } from 'http';

import { API_URI } from '@/lib/const';

// =============================================================================
// Cookie Name Utilities
// =============================================================================

/**
 * Sanitize a storage key to be used as a cookie name.
 * Replaces characters not allowed in cookie names with underscores.
 */
const sanitizeCookieName = (key: string): string => {
  return key.replace(/[^a-zA-Z0-9_-]/g, '_');
};

/**
 * Build the full cookie name for an SDK storage key.
 * The SDK prefixes all keys with the Saleor API URL.
 */
const buildSdkCookieName = (suffix: string): string => {
  const key = `${API_URI}+${suffix}`;
  return sanitizeCookieName(key);
};

// SDK storage key suffixes (the SDK adds API_URI prefix to these)
const SDK_REFRESH_TOKEN_SUFFIX = 'saleor_auth_module_refresh_token';
const SDK_ACCESS_TOKEN_SUFFIX = 'saleor_auth_access_token';
const SDK_AUTH_STATE_SUFFIX = 'saleor_auth_module_auth_state';

// Full cookie names with API_URI prefix (used for SSR cookie reading)
export const AUTH_REFRESH_TOKEN_COOKIE = buildSdkCookieName(
  SDK_REFRESH_TOKEN_SUFFIX,
);
export const AUTH_ACCESS_TOKEN_COOKIE = buildSdkCookieName(
  SDK_ACCESS_TOKEN_SUFFIX,
);
export const AUTH_STATE_COOKIE = buildSdkCookieName(SDK_AUTH_STATE_SUFFIX);

// =============================================================================
// Cookie Configuration
// =============================================================================

const COOKIE_MAX_AGE_REFRESH = 60 * 60 * 24 * 30; // 30 days for refresh token
// Access token cookie duration: Set to 30 days to match refresh token.
// Although the JWT itself expires in ~5 minutes (controlled by Saleor),
// we keep the cookie longer so we can still decode user_id for SSR purposes.
// The @saleor/auth-sdk will refresh the token when needed for actual API calls.
const COOKIE_MAX_AGE_ACCESS = 60 * 60 * 24 * 30; // 30 days (BEC-20960)

/**
 * StorageRepository interface as defined by @saleor/auth-sdk
 */
export interface StorageRepository {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

/**
 * Determines if we're running in a production environment
 */
const isProduction = (): boolean => {
  return process.env.NEXT_PUBLIC_ENVIRONMENT === 'PRD';
};

/**
 * Get cookie domain for production (shared across subdomains)
 */
const getCookieDomain = (): string | undefined => {
  if (!isProduction()) {
    return undefined; // Host-only cookie for non-production
  }

  // In production, use the root domain for cross-subdomain sharing
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Extract root domain (e.g., 'breitling.com' from 'www.breitling.com')
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
  }

  return undefined;
};

/**
 * Build cookie attributes string
 */
const buildCookieAttributes = (maxAge: number): string => {
  const parts: string[] = [`path=/`, `max-age=${maxAge}`, `SameSite=Strict`];

  // Add Secure flag in production (required for SameSite=Strict in most browsers)
  if (isProduction()) {
    parts.push('Secure');
  }

  const domain = getCookieDomain();
  if (domain) {
    parts.push(`domain=${domain}`);
  }

  return parts.join('; ');
};

/**
 * Cookie-based storage repository for refresh tokens.
 *
 * This implementation stores the refresh token in a cookie that:
 * - Is accessible to JavaScript (for @saleor/auth-sdk compatibility)
 * - Uses SameSite=Strict for CSRF protection
 * - Uses Secure flag in production
 * - Has a 30-day expiration (matching typical refresh token lifetime)
 *
 * IMPORTANT: This storage is KEY-AWARE. The @saleor/auth-sdk uses multiple keys:
 * - 'saleor_auth_module_auth_state' for auth state
 * - 'saleor_auth_module_refresh_token' for the actual refresh token JWT
 *
 * We map SDK keys to cookie names to store each value separately.
 */
export class CookieRefreshTokenStorage implements StorageRepository {
  getItem(key: string): string | null {
    if (typeof document === 'undefined') {
      return null; // SSR: will use getServerSideRefreshToken instead
    }

    try {
      const cookieName = sanitizeCookieName(key);
      const cookies = cookie.parse(document.cookie);
      return cookies[cookieName] ?? null;
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    if (typeof document === 'undefined') {
      return; // SSR: cannot set cookies directly
    }

    try {
      const cookieName = sanitizeCookieName(key);
      const attributes = buildCookieAttributes(COOKIE_MAX_AGE_REFRESH);
      document.cookie = `${cookieName}=${encodeURIComponent(
        value,
      )}; ${attributes}`;
    } catch {
      // Cookie setting failed silently
    }
  }

  removeItem(key: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      const cookieName = sanitizeCookieName(key);
      // Set max-age=0 to delete the cookie
      const attributes = buildCookieAttributes(0);
      document.cookie = `${cookieName}=; ${attributes}`;
    } catch {
      // Cookie removal failed silently
    }
  }
}

/**
 * Cookie-based storage repository for access tokens.
 *
 * Access tokens are shorter-lived and could alternatively be stored in memory.
 * Storing in cookies allows SSR to detect authentication state without
 * requiring a token refresh on every server render.
 *
 * Note: Access tokens in cookies increase request header size.
 * Consider using in-memory storage if header size becomes an issue.
 *
 * This storage is KEY-AWARE to properly support the @saleor/auth-sdk.
 */
export class CookieAccessTokenStorage implements StorageRepository {
  getItem(key: string): string | null {
    if (typeof document === 'undefined') {
      return null; // SSR: will use getServerSideAccessToken instead
    }

    try {
      const cookieName = sanitizeCookieName(key);
      const cookies = cookie.parse(document.cookie);
      return cookies[cookieName] ?? null;
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      const cookieName = sanitizeCookieName(key);
      const attributes = buildCookieAttributes(COOKIE_MAX_AGE_ACCESS);
      document.cookie = `${cookieName}=${encodeURIComponent(
        value,
      )}; ${attributes}`;
    } catch {
      // Cookie setting failed silently
    }
  }

  removeItem(key: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      const cookieName = sanitizeCookieName(key);
      const attributes = buildCookieAttributes(0);
      document.cookie = `${cookieName}=; ${attributes}`;
    } catch {
      // Cookie removal failed silently
    }
  }
}

// Singleton instances
let refreshTokenStorage: CookieRefreshTokenStorage | null = null;
let accessTokenStorage: CookieAccessTokenStorage | null = null;

/**
 * Get the singleton refresh token storage instance
 */
export const getRefreshTokenStorage = (): CookieRefreshTokenStorage => {
  if (!refreshTokenStorage) {
    refreshTokenStorage = new CookieRefreshTokenStorage();
  }
  return refreshTokenStorage;
};

/**
 * Get the singleton access token storage instance
 */
export const getAccessTokenStorage = (): CookieAccessTokenStorage => {
  if (!accessTokenStorage) {
    accessTokenStorage = new CookieAccessTokenStorage();
  }
  return accessTokenStorage;
};

// =============================================================================
// Server-Side Utilities (for SSR/getServerSideProps)
// =============================================================================

/**
 * Parse cookies from an incoming HTTP request.
 * Used in getServerSideProps or API routes.
 */
export const parseRequestCookies = (
  req: IncomingMessage,
): Record<string, string> => {
  const cookieHeader = req.headers.cookie;
  if (!cookieHeader) {
    return {};
  }
  return cookie.parse(cookieHeader);
};

/**
 * Get the refresh token from server-side request cookies.
 *
 * @param req - The incoming HTTP request (from getServerSideProps context)
 * @returns The refresh token or null if not present
 */
export const getServerSideRefreshToken = (
  req: IncomingMessage,
): string | null => {
  const cookies = parseRequestCookies(req);
  const token = cookies[AUTH_REFRESH_TOKEN_COOKIE];
  return token ? decodeURIComponent(token) : null;
};

/**
 * Get the access token from server-side request cookies.
 *
 * @param req - The incoming HTTP request (from getServerSideProps context)
 * @returns The access token or null if not present
 */
export const getServerSideAccessToken = (
  req: IncomingMessage,
): string | null => {
  const cookies = parseRequestCookies(req);
  const token = cookies[AUTH_ACCESS_TOKEN_COOKIE];
  return token ? decodeURIComponent(token) : null;
};

/**
 * Check if the user is authenticated on the server side.
 *
 * This checks for the presence of a refresh token, which indicates
 * the user has an active session (even if the access token is expired).
 *
 * @param req - The incoming HTTP request
 * @returns true if a refresh token is present
 */
export const isAuthenticatedServerSide = (req: IncomingMessage): boolean => {
  return getServerSideRefreshToken(req) !== null;
};

/**
 * Decode JWT payload without verification.
 * Used to extract user information from the token for SSR.
 *
 * WARNING: This does NOT verify the token signature.
 * Only use for reading claims, not for authorization decisions.
 */
export const decodeTokenPayload = (
  token: string,
): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = Buffer.from(base64, 'base64').toString('utf-8');
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Extract user_id from a JWT payload if available.
 */
const extractUserIdFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }

  // Saleor uses 'user_id' claim
  const userId = payload.user_id;
  if (typeof userId === 'string') {
    return userId;
  }

  return null;
};

/**
 * Get user ID from auth tokens on server side.
 *
 * First tries the access token, then falls back to the refresh token.
 * This ensures we can still get the user ID even after the access token
 * cookie expires (after 1 hour), as long as the refresh token is valid.
 *
 * The Saleor JWT typically contains a 'user_id' claim in both tokens.
 *
 * @param req - The incoming HTTP request
 * @returns The user ID or null if not available
 */
export const getServerSideUserId = (req: IncomingMessage): string | null => {
  // Try access token first (preferred, more recent)
  const accessToken = getServerSideAccessToken(req);
  if (accessToken) {
    const payload = decodeTokenPayload(accessToken);
    const userId = extractUserIdFromPayload(payload);
    if (userId) {
      return userId;
    }
  }

  // Fallback to refresh token if access token is unavailable or doesn't have user_id
  // This handles the case where access token cookie has expired (after 1 hour)
  // but refresh token is still valid (30 days)
  const refreshToken = getServerSideRefreshToken(req);
  if (refreshToken) {
    const payload = decodeTokenPayload(refreshToken);
    const userId = extractUserIdFromPayload(payload);
    if (userId) {
      return userId;
    }
  }

  return null;
};
